// ============================================
// プレイヤー関連状態管理
// ============================================

import type { DefeatedByType } from '../types/game-types';

// --- localStorage キー ---
const STORAGE_KEY = 'laneShooterHighScore';

// --- スコアとコンボ ---
let _score: number = 0;
let _highScore: number = 0;
let _combo: number = 0;
let _maxCombo: number = 0;

// --- パワーレベル ---
let _powerLevel: number = 1;
let _powerStock: number = 0;

// --- シールド ---
let _shieldActive: boolean = false;
let _shieldTimer: number = 0;
let _shieldCharging: boolean = false;

// --- 画面揺れ ---
let _screenShake: number = 0;

// --- ウェーブと撃破数 ---
let _wave: number = 1;
let _enemiesDefeated: number = 0;
let _waveEnemiesDefeated: number = 0;

// 敵タイプ別撃破数（リザルト用）
let _defeatedByType: DefeatedByType = {
    normal: 0,
    tank: 0,
    fast: 0,
    armored: 0,
    speedy: 0,
    laser: 0,
    mirror: 0,
    boss: 0
};

// --- ランク ---
let _rank: string = 'D';

// --- 残機 ---
let _lives: number = 3;

// --- ゲームオーバー用変数 ---
let _gameOverTimer: number = 0;
let _canRestart: boolean = false;
let _deathAnimTimer: number = 0;

// --- 死亡フェード ---
let _dyingFadeAlpha: number = 1;

// ============================================
// Getters and Setters
// ============================================

export const getScore = (): number => _score;
export const setScore = (val: number): void => {
    _score = val;
};

export const getHighScore = (): number => _highScore;
export const setHighScore = (val: number): void => {
    _highScore = val;
    // localStorageにも保存
    try {
        localStorage.setItem(STORAGE_KEY, String(val));
    } catch {
        // localStorageが利用できない場合は無視
    }
};

/**
 * loadHighScoreFromStorage() - localStorageからハイスコアを読み込み
 */
export const loadHighScoreFromStorage = (): void => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored !== null) {
            const parsed = parseFloat(stored);
            if (!isNaN(parsed) && parsed >= 0) {
                _highScore = parsed;
            }
        }
    } catch {
        // localStorageが利用できない場合は無視
    }
};

/**
 * clearHighScore() - ハイスコアをリセット（メモリ＋localStorage）
 */
export const clearHighScore = (): void => {
    _highScore = 0;
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        // localStorageが利用できない場合は無視
    }
};

export const getCombo = (): number => _combo;
export const setCombo = (val: number): void => {
    _combo = val;
};

export const getMaxCombo = (): number => _maxCombo;
export const setMaxCombo = (val: number): void => {
    _maxCombo = val;
};

export const getPowerLevel = (): number => _powerLevel;
export const setPowerLevel = (val: number): void => {
    _powerLevel = val;
};

export const getPowerStock = (): number => _powerStock;
export const setPowerStock = (val: number): void => {
    _powerStock = val;
};

export const getShieldActive = (): boolean => _shieldActive;
export const setShieldActive = (val: boolean): void => {
    _shieldActive = val;
};

export const getShieldTimer = (): number => _shieldTimer;
export const setShieldTimer = (val: number): void => {
    _shieldTimer = val;
};

export const getShieldCharging = (): boolean => _shieldCharging;
export const setShieldCharging = (val: boolean): void => {
    _shieldCharging = val;
};

export const getScreenShake = (): number => _screenShake;
export const setScreenShake = (val: number): void => {
    _screenShake = val;
};

export const getWave = (): number => _wave;
export const setWave = (val: number): void => {
    _wave = val;
};

export const getEnemiesDefeated = (): number => _enemiesDefeated;
export const setEnemiesDefeated = (val: number): void => {
    _enemiesDefeated = val;
};

export const getWaveEnemiesDefeated = (): number => _waveEnemiesDefeated;
export const setWaveEnemiesDefeated = (val: number): void => {
    _waveEnemiesDefeated = val;
};

export const getDefeatedByType = (): DefeatedByType => _defeatedByType;
export const setDefeatedByType = (val: DefeatedByType): void => {
    _defeatedByType = val;
};
export const incrementDefeatedByType = (type: keyof DefeatedByType): void => {
    if (_defeatedByType[type] !== undefined) {
        _defeatedByType[type]++;
    }
};

export const getGameOverTimer = (): number => _gameOverTimer;
export const setGameOverTimer = (val: number): void => {
    _gameOverTimer = val;
};

export const getCanRestart = (): boolean => _canRestart;
export const setCanRestart = (val: boolean): void => {
    _canRestart = val;
};

export const getRank = (): string => _rank;
export const setRank = (val: string): void => {
    _rank = val;
};

export const getLives = (): number => _lives;
export const setLives = (val: number): void => {
    _lives = val;
};

export const getDeathAnimTimer = (): number => _deathAnimTimer;
export const setDeathAnimTimer = (val: number): void => {
    _deathAnimTimer = val;
};

export const getDyingFadeAlpha = (): number => _dyingFadeAlpha;
export const setDyingFadeAlpha = (val: number): void => {
    _dyingFadeAlpha = val;
};
